import { ActivityTrackerEventType } from '../activityTracker/constants';
import { useActivityTracker } from '../activityTracker/useActivityTracker';
import { useCurrentUser } from '../user/useCurrentUser';

import { type BagViewArgs } from './types';

import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';

export function useBagTracking() {
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent: sendATEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const {
    gtm: { sendEvent: sendGTMEvent },
  } = useExternalTracking();

  function sendBagViewEvent({ productIds, soldProductIds }: BagViewArgs) {
    sendATEvent(ActivityTrackerEventType.BAG_VIEW, {
      schemaVersion: '4.0',
      productIds,
      soldProductIds,
    });
  }

  function sendAddItemToBagAction({
    baggedProductIds,
    baggedProductVariants,
    productIds,
    eventId,
  }: {
    baggedProductIds: number[];
    baggedProductVariants: Array<number | null>;
    productIds: number[];
    eventId: string;
  }) {
    sendATEvent(
      ActivityTrackerEventType.ADD_ITEM_TO_BAG_ACTION,
      {
        schemaVersion: '3.0',
        action: 'add',
        baggedProductIds,
        // an array of the existing bagged product quantities AND the newly added product quantity - we only support a qty of 1 for mvp
        baggedProductQuantities: baggedProductIds.map(() => 1),
        baggedProductVariants,
        productIds,
      },
      {
        baseEventOverrides: { id: eventId },
      }
    );
  }

  function sendAddToBagGTMEvent({
    id,
    currency,
    name,
    price,
    value,
    item_category,
    variantId,
    eventId,
  }: {
    id: string;
    currency: string;
    name: string;
    price: string;
    value: string;
    item_category?: string;
    variantId?: number;
    eventId: string;
  }) {
    const item_id = variantId ? `${id}-${variantId}` : id;

    sendGTMEvent({
      event: 'add_to_cart',
      event_id: eventId,
      num_items: 1,
      content_ids: [item_id],
      contents: [{ id: item_id, item_price: price, quantity: 1 }],
      item_category,
      item_id,
      ecommerce: {
        currency,
        items: [
          {
            item_id,
            item_name: name,
            price,
            quantity: 1,
          },
        ],
        value,
      },
    });
  }

  function sendQuickAccessBagViewEvent() {
    sendATEvent(ActivityTrackerEventType.WEB_BAG_QUICK_ACCESS_VIEW, {
      schemaVersion: '1.0',
    });
  }

  function sendBagViewAction(bagEventType: 'quickAccessBag' | 'nav' | 'pdp') {
    sendATEvent(ActivityTrackerEventType.WEB_BAG_VIEW_ACTION, {
      schemaVersion: '1.0',
      bagEventType,
    });
  }

  function sendBagCheckoutAction(bagEventType: 'quickAccessBag' | 'bag') {
    sendATEvent(ActivityTrackerEventType.WEB_BAG_CHECKOUT_ACTION, {
      schemaVersion: '1.0',
      bagEventType,
    });
  }

  return {
    sendBagViewEvent,
    sendAddItemToBagAction,
    sendAddToBagGTMEvent,
    sendQuickAccessBagViewEvent,
    sendBagViewAction,
    sendBagCheckoutAction,
  };
}
